import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { HERO_IMAGE } from "../data/services";
import { WHATSAPP_URL } from "../i18n/translations";

const WhatsAppGlyph = ({ className = "h-5 w-5" }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
  </svg>
);

export const Hero = ({ t }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0]);

  const scrollToContact = () => {
    if (window.__lenis) window.__lenis.scrollTo("#contact", { offset: -72, duration: 1.6 });
    else document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section ref={ref} id="top" data-testid="hero-section" className="relative flex min-h-screen items-center overflow-hidden">
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img src={HERO_IMAGE} alt="" className="h-[125%] w-full object-cover opacity-25" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0D14]/70 via-[#0A0D14]/40 to-[#0A0D14]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0D14]/90 via-transparent to-[#0A0D14]/60" />
      </motion.div>

      <div className="pointer-events-none absolute -right-[4%] top-1/2 w-[46vw] max-w-[720px] -translate-y-1/2 select-none">
        <motion.img
          src="/logo-icon-trans.png"
          alt=""
          aria-hidden="true"
          initial={{ opacity: 0, scale: 0.94 }}
          animate={{ opacity: 0.14, scale: 1, y: [0, -16, 0] }}
          transition={{
            opacity: { duration: 2, delay: 0.6 },
            scale: { duration: 2, delay: 0.6 },
            y: { duration: 14, repeat: Infinity, ease: "easeInOut" },
          }}
          className="w-full"
        />
      </div>

      <motion.div
        aria-hidden="true"
        animate={{ y: [0, -24, 0], opacity: [0.5, 0.9, 0.5] }}
        transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
        className="absolute right-[12%] top-[18%] h-72 w-72 rounded-full bg-amber-400/15 blur-[110px]"
      />
      <motion.div
        aria-hidden="true"
        animate={{ y: [0, 20, 0], opacity: [0.3, 0.6, 0.3] }}
        transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
        className="absolute bottom-[10%] left-[6%] h-80 w-80 rounded-full bg-amber-500/10 blur-[130px]"
      />

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-5 pb-24 pt-36 sm:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-8 inline-flex items-center gap-3 rounded-full border border-amber-400/30 bg-amber-400/5 px-4 py-2"
        >
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-amber-400" />
          <span data-testid="hero-eyebrow" className="text-xs font-semibold uppercase tracking-[0.28em] text-amber-300">{t.hero.eyebrow}</span>
        </motion.div>

        <h1 className="font-display text-4xl font-bold leading-[1.02] tracking-tight text-white sm:text-6xl lg:text-7xl">
          {t.hero.lines.map((line, i) => (
            <span key={i} className="block overflow-hidden pb-1">
              <motion.span
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.95, delay: 0.25 + i * 0.14, ease: [0.22, 1, 0.36, 1] }}
                className={`block ${i === 1 ? "gold-text italic" : ""}`}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          data-testid="hero-subtitle"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.85 }}
          className="mt-7 max-w-xl text-base leading-relaxed text-slate-300 sm:text-lg"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.05 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-consult-button"
            onClick={scrollToContact}
            className="group inline-flex items-center gap-2 rounded-full bg-amber-400 px-7 py-3.5 text-sm font-bold text-slate-950 transition-colors duration-300 hover:bg-amber-300"
          >
            {t.hero.ctaPrimary}
            <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
          <a
            data-testid="hero-whatsapp-button"
            href={`${WHATSAPP_URL}?text=${encodeURIComponent(t.whatsapp_prefill)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 text-sm font-semibold text-white transition-colors duration-300 hover:border-amber-400/60 hover:text-amber-300"
          >
            <WhatsAppGlyph className="h-4 w-4 text-emerald-400" />
            {t.hero.ctaSecondary}
          </a>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1.3 }}
          className="mt-16 grid max-w-2xl grid-cols-3 gap-6 border-t border-white/10 pt-8"
        >
          {t.hero.stats.map((s, i) => (
            <div key={i} data-testid={`hero-stat-${i}`}>
              <div className="font-display text-xl font-bold text-amber-400 sm:text-2xl">{s.value}</div>
              <div className="mt-1 text-[11px] uppercase tracking-[0.18em] text-slate-400 sm:text-xs">{s.label}</div>
            </div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
